import { isDefined } from "@clipboard-health/util-ts";
import { type TimesheetEntry } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/hooks/useTimesheetEntry";
import { calculateHasPendingHoursAdjustment } from "@src/appV2/redesign/WorkerShifts/Timekeeping/Timesheet/utils/calculateHasPendingHoursAdjustment";
import { parseISO } from "date-fns";
import { useMemo } from "react";

import { useGetProvisionalExtraWorkedTimeRequest } from "../api/useGetProvisionalExtraWorkedTimeRequest";

interface UsePendingHoursAdjustmentParams {
  shiftId: string | undefined;
  isMissedPunchRequestEnabledV2: boolean;
  /** The original recorded timesheet (hcp_clock_in/out). Used as the "verified" baseline. */
  formTimesheet: TimesheetEntry | undefined;
  /** The currently displayed clock-in time (may equal the form value if no adjustment). */
  formClockInTime: Date | undefined;
  formClockOutTime: Date | undefined;
  formBreakStartTime: Date | undefined;
  formBreakEndTime: Date | undefined;
  /**
   * Fallback clock-in time used as the "original" baseline when formTimesheet is undefined
   * (e.g. unverified shifts with no IP clock-in). Typically the scheduled shift start.
   */
  recordedClockInTime?: Date;
}

export interface UsePendingHoursAdjustmentResult {
  /** Date parsed from the provisional extra worked time request's start, or undefined when none exists. */
  adjustedClockInTime: Date | undefined;
  /** The provisional extra worked time request entity, if one exists for this shift. */
  existingRequest: { id: string; attributes: { start: string; reason: string } } | undefined;
  /**
   * True when `isMissedPunchRequestEnabledV2` is on AND the adjusted timesheet claims more paid
   * minutes than the verified (original) timesheet. Use this to gate both the alert banner and the
   * Verified/Pending breakdown in TimesheetTimeEntries.
   */
  hasPendingHoursAdjustment: boolean;
  /**
   * Pass as `originalClockInTime` on TimesheetTimeEntries.
   * Only set when the feature is on and an adjustment exists.
   */
  originalClockInTime: Date | undefined;
  /**
   * Pass as `verifiedTimesheet` on TimesheetTimeEntries.
   * Only set when the feature is on and more hours are being claimed.
   */
  verifiedTimesheetForEntries: TimesheetEntry | undefined;
}

/**
 * Consolidates the pending-hours-adjustment data fetching and derived state
 * shared across SubmitTimesheetPage, SubmitUnverifiedTimesheetPage.
 */
export function usePendingHoursAdjustment({
  shiftId,
  isMissedPunchRequestEnabledV2,
  formTimesheet,
  formClockInTime,
  formClockOutTime,
  formBreakStartTime,
  formBreakEndTime,
  recordedClockInTime,
}: UsePendingHoursAdjustmentParams): UsePendingHoursAdjustmentResult {
  const { data: provisionalExtraWorkedTimeData } = useGetProvisionalExtraWorkedTimeRequest(
    shiftId ?? "",
    { enabled: isMissedPunchRequestEnabledV2 && isDefined(shiftId) && shiftId.length > 0 }
  );

  const existingRequest = provisionalExtraWorkedTimeData?.data[0];

  const adjustedClockInTime = useMemo(
    () =>
      existingRequest?.attributes.start ? parseISO(existingRequest.attributes.start) : undefined,
    [existingRequest?.attributes.start]
  );

  const displayClockInTime = adjustedClockInTime ?? formClockInTime;

  const rawHasPendingHoursAdjustment =
    isDefined(formTimesheet) && isDefined(displayClockInTime) && isDefined(formClockOutTime)
      ? calculateHasPendingHoursAdjustment({
          verifiedTimesheet: formTimesheet,
          adjustedTimesheet: {
            clockInTime: displayClockInTime,
            clockOutTime: formClockOutTime,
            breakStartTime: formBreakStartTime,
            breakEndTime: formBreakEndTime,
          },
        })
      : false;

  // Incorporate the feature flag so all callers can use hasPendingHoursAdjustment directly
  // without needing to re-apply the flag check themselves.
  const hasPendingHoursAdjustment = isMissedPunchRequestEnabledV2 && rawHasPendingHoursAdjustment;

  return {
    adjustedClockInTime,
    existingRequest,
    hasPendingHoursAdjustment,
    originalClockInTime:
      isMissedPunchRequestEnabledV2 &&
      (isDefined(adjustedClockInTime) ||
        (isDefined(formTimesheet) &&
          isDefined(formClockInTime) &&
          formClockInTime < formTimesheet.clockInTime))
        ? (formTimesheet?.clockInTime ?? recordedClockInTime)
        : undefined,
    verifiedTimesheetForEntries: hasPendingHoursAdjustment ? formTimesheet : undefined,
  };
}
